import {useRef} from 'react';

import {
    Alert,
    Stack,
} from '@mantine/core';
import {useNavigate} from 'react-router-dom';

import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import {DataTableColumn} from "mantine-datatable";

import {usePipelines} from '../hooks/usePipelines.ts';
import {usePipelinesFilter} from '../hooks/usePipelinesFilter.ts';
import {usePipelinesSelection} from '../hooks/usePipelinesSelection.ts';
import {usePipelinesSearch} from "../hooks/usePipelinesSearch.ts";
import {usePipelinesTableColumns} from "../hooks/usePipelinesTableColumns.tsx";
import {useVerifiedPermissions} from "../../../../hooks/useVerifiedPermissions.ts";
import {rbacActions} from "../../../../config/rbac/actions.ts";
import {Pipeline} from '../types.ts';
import config from '../config';

export const RecordsTable = () => {

    const {allowedActions} = useVerifiedPermissions()

    const navigate = useNavigate();

    const {filters} = usePipelinesFilter();

    const {
        // The full loaded set BEFORE keyword filtering — the window the
        // footer's match count is measured against.
        items,
        visibleItems: records,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        isFetchingNextPage,
        nextToken,
        fetchNextPage,
    } = usePipelines(filters);

    const {
        columns,
    } = usePipelinesTableColumns();

    // Same module-scoped store the list hook filters on — this component owns
    // the input, usePipelines owns the filtering.
    const {
        keyword,
        handleFilterByKeyword,
    } = usePipelinesSearch();

    const {
        selection,
        handleRowSelection,
        handleClearSelection
    } = usePipelinesSelection();

    // Checkboxes appear only for an operator who can actually delete. Offering
    // selection to someone with no action to apply to it is a dead affordance.
    let selectedRecords: Pipeline[] = [];
    if (allowedActions.includes(rbacActions.DeletePipeline)) {
        selectedRecords = records.filter(record => selection[record.id] === true)
    }

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Two distinct empty states: nothing exists yet, versus nothing matched the
    // search. Collapsing them into one message tells an operator who mistyped a
    // keyword that they have no pipelines at all.
    const noRecordsText = keyword
        ? `No pipelines match "${keyword}" in the records loaded so far.`
        : 'No pipelines yet.';

    return (
        <Stack>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title='Could not load pipelines'>
                    The pipelines API did not return the list. Try Reload; if it persists the
                    API may be unavailable in this environment.
                </Alert>
            )}

            <MantineDataTable
                columns={columns as DataTableColumn[]}
                records={records}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={(items) => {
                    handleClearSelection();
                    items.forEach((item: any) => {
                        handleRowSelection(item.id, true)
                    })
                }}
                // Sorting is client-side over the pages loaded so far — the same
                // scope the keyword filter works on. Token pagination cannot
                // reorder rows the browser has not fetched.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                fetching={loading}
                noRecordsText={noRecordsText}
                onScrollToBottom={() => fetchNextPage()}
                scrollViewportRef={scrollViewportRef}
                onRowClick={({record}) =>
                    navigate(`${config.basePath}/${encodeURIComponent((record as Pipeline).id)}`)}
            />

            <DataTableTokenPagination
                loadedCount={records.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                isFetchingMore={isFetchingNextPage}
                nextToken={nextToken}
                handleLoadMore={fetchNextPage}
                noun='pipeline'
            />
        </Stack>
    );
};
